import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { registerInvestor, getMyInvestor } from "@/lib/investor.functions";
import { isDisposableEmail } from "@/lib/email-policy";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/for-investors")({
  head: () => ({
    meta: [
      { title: "Vetted Early-Stage Deal Flow for Investors - DeckScore" },
      {
        name: "description",
        content:
          "See vetted, deck-scored early-stage startups matched to your thesis. DeckScore pre-screens first-time founders so you get qualified deal flow, not cold inbound.",
      },
      { property: "og:title", content: "Vetted Early-Stage Deal Flow for Investors - DeckScore" },
      {
        property: "og:description",
        content:
          "See vetted, deck-scored early-stage startups matched to your thesis. DeckScore pre-screens first-time founders so you get qualified deal flow, not cold inbound.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://deckscore.ai/for-investors" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://deckscore.ai/for-investors" }],
  }),
  component: ForInvestors,
});

const DRAFT_KEY = "investor_reg_draft";

type Draft = {
  name: string;
  type: string;
  thesis: string;
  region: string;
  contact: string;
  openToProgram: boolean;
};

const BENEFITS: { title: string; body: string; tone: "peach" | "sky" | "surface" | "mint" | "lavender" }[] = [
  {
    title: "A 0-100 investor-readiness score",
    body: "Every deck lands with an honest score - the strongest rise to the top of your inbox.",
    tone: "peach",
  },
  {
    title: "The full analysis, not a founder's spin",
    body: "Strengths, gaps, red flags, slide-by-slide. Decide who's worth a call in 30 seconds.",
    tone: "lavender",
  },
  {
    title: "A clean one-pager, auto-generated",
    body: "Ready to forward to a partner. No formatting, no chasing, no follow-ups.",
    tone: "surface",
  },
  {
    title: "Zero effort - founders do the work",
    body: "Share one page. Results land in your inbox. You never touch a spreadsheet.",
    tone: "mint",
  },
  {
    title: "Private by default - never listed publicly",
    body: "Only founders you share your page with can reach you. DeckScore never publishes a directory of investors.",
    tone: "lavender",
  },
];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ForInvestors() {
  const navigate = useNavigate();
  const runRegister = useServerFn(registerInvestor);
  const runGet = useServerFn(getMyInvestor);

  const [checking, setChecking] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("vc");
  const [thesis, setThesis] = useState("");
  const [contact, setContact] = useState("");
  const [region, setRegion] = useState("");
  const [openToProgram, setOpenToProgram] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicSentTo, setMagicSentTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptedAutoRef = useRef(false);

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw) as Partial<Draft>;
        if (d.name) setName(d.name);
        if (d.type) setType(d.type);
        if (d.thesis) setThesis(d.thesis);
        if (d.region) setRegion(d.region);
        if (d.contact) setContact(d.contact);
        if (typeof d.openToProgram === "boolean") setOpenToProgram(d.openToProgram);
      }
    } catch { /* ignore */ }
  }, []);

  // Session check + auto-resume after magic-link return
  useEffect(() => {
    (async () => {
      try {
        const { waitForSession } = await import("@/lib/auth-ready");
        const session = await waitForSession();
        if (!session) {
          setChecking(false);
          return;
        }
        // Already-registered investor: skip everything
        const { investor } = await runGet();
        if (investor) {
          navigate({ to: "/investor" });
          return;
        }
        // Signed in but no investor row - try auto-resume from draft
        const raw = (() => { try { return localStorage.getItem(DRAFT_KEY); } catch { return null; } })();
        if (raw && !attemptedAutoRef.current) {
          attemptedAutoRef.current = true;
          let d: Partial<Draft> | null = null;
          try { d = JSON.parse(raw); } catch { d = null; }
          if (d && d.name && d.name.trim()) {
            setFinalizing(true);
            setChecking(false);
            try {
              await runRegister({
                data: {
                  name: d.name.trim(),
                  type: d.type || "vc",
                  thesis: (d.thesis || "").trim() || null,
                  region: (d.region || "").trim() || null,
                  contact_email: session.user?.email ?? null,
                  open_to_program: !!d.openToProgram,
                },
              });
              try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
              navigate({ to: "/investor" });
              return;
            } catch (err) {
              setFinalizing(false);
              setError(err instanceof Error ? err.message : String(err));
              // Keep draft so user can edit and retry
              setContact(session.user?.email ?? contact);
              return;
            }
          }
        }
        // Signed in, no draft - prefill contact
        if (session.user?.email) setContact(session.user.email);
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function saveDraft(overrides?: Partial<Draft>) {
    const d: Draft = {
      name, type, thesis, region, contact, openToProgram,
      ...overrides,
    };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* ignore */ }
  }

  async function onClaim(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    const trimmedEmail = contact.trim();
    if (!trimmedName) { setError("Please enter your firm or fund name."); return; }
    if (!emailRe.test(trimmedEmail)) { setError("Please enter a valid work email."); return; }
    if (isDisposableEmail(trimmedEmail)) { setError("Please use a permanent work email - temporary/disposable addresses aren't allowed."); return; }
    

    setSaving(true);
    saveDraft({ name: trimmedName, contact: trimmedEmail });

    try {
      // If already signed in with matching email, register directly
      const { data: sessData } = await supabase.auth.getSession();
      const sessionEmail = sessData.session?.user?.email?.toLowerCase() ?? null;
      if (sessionEmail && sessionEmail === trimmedEmail.toLowerCase()) {
        await runRegister({
          data: {
            name: trimmedName,
            type,
            thesis: thesis.trim() || null,
            region: region.trim() || null,
            contact_email: trimmedEmail,
            open_to_program: openToProgram,
          },
        });
        try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
        navigate({ to: "/investor" });
        return;
      }

      // Otherwise send magic link
      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
        options: { emailRedirectTo: new URL("/for-investors", window.location.origin).toString() },
      });
      if (otpErr) throw otpErr;
      setMagicSentTo(trimmedEmail);
      setMagicSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const inputCls =
    "min-h-[48px] w-full rounded-xl border border-line bg-surface px-3.5 text-[14.5px] text-ink placeholder:text-ink-tertiary focus:border-accent focus-visible:outline-none";
  const primaryBtn =
    "inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent px-6 text-[15.5px] font-bold text-accent-ink transition hover:-translate-y-px hover:bg-accent-press disabled:opacity-60";

  const toneBg: Record<string, string> = {
    peach: "bg-peach",
    sky: "bg-sky",
    surface: "bg-surface",
    mint: "bg-accent-mint",
    lavender: "bg-accent-lavender",
  };

  return (
    <div className="min-h-screen flex flex-col bg-bg text-ink overflow-x-hidden">
      <header className="sticky top-0 z-40 border-b border-line bg-bg/85 backdrop-blur-md">
        <div className="max-w-[1120px] mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="inline-block h-[22px] w-[22px] rounded-md bg-accent" />
            <span className="font-display text-[16px] font-semibold">DeckScore</span>
          </Link>
          <Link to="/" className="text-[14px] font-medium text-muted-ink hover:text-ink">
            ← Back to home
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* HERO */}
        <section className="bg-peach px-5 pt-14 pb-16 sm:pt-20 sm:pb-24">
          <div className="max-w-[1120px] mx-auto grid gap-10 md:grid-cols-[1.05fr_0.95fr] md:gap-14 items-center">
            <div>
              <span className="font-script text-accent text-[24px] leading-none">for investors</span>
              <h1 className="font-display text-[clamp(30px,4.6vw,52px)] font-semibold mt-3 leading-[1.05] tracking-tight">
                Turn every founder who pitches you into scored, ready-to-skim dealflow.
              </h1>
              <p className="text-[17px] sm:text-[18.5px] text-muted-ink mt-5 max-w-[560px] leading-[1.55]">
                Share your private DeckScore page with founders. The moment they run their
                deck, you get an honest investor-readiness score, the full analysis, and a
                one-pager - before you spend a single minute. Decide in 30 seconds.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1.5 text-[14px] text-muted-ink">
                <span>✓ Free forever</span>
                <span>✓ Never listed publicly</span>
                <span>✓ 60 seconds to claim</span>
              </div>

              <div className="mt-6 max-w-[520px]">
                <a href="#claim" className={primaryBtn}>
                  Claim my dealflow page →
                </a>
                <p className="text-[12.5px] text-ink-tertiary mt-2">
                  Takes about 60 seconds. We'll email you to verify.
                </p>
              </div>
            </div>

            {/* Preview card */}
            <div
              className="rounded-[20px] border border-line bg-surface p-5 shadow-[0_24px_60px_-18px_rgba(0,0,0,0.22)] ring-1 ring-black/5"
              aria-hidden="true"
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2.5 text-sm text-muted-ink">
                  <span className="h-2.5 w-2.5 rounded-full bg-accent" />
                  <span className="font-mono text-[12.5px]">deckscore.ai/your-fund</span>
                </div>
                <div className="rounded-full bg-accent/10 px-2.5 py-1 text-xs font-bold text-accent">
                  Founder just pitched
                </div>
              </div>
              <div className="mb-5 flex items-center gap-5">
                <div className="relative h-[110px] w-[110px] flex-none">
                  <svg width="110" height="110" viewBox="0 0 118 118" className="-rotate-90">
                    <circle cx="59" cy="59" r="52" fill="none" stroke="#E3DFDA" strokeWidth="12" />
                    <circle
                      cx="59" cy="59" r="52" fill="none" stroke="#B79AEF" strokeWidth="12"
                      strokeLinecap="round" strokeDasharray="326.7" strokeDashoffset="65"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[30px] font-semibold tracking-tight leading-none">80</span>
                    <span className="text-[10.5px] uppercase tracking-wider text-muted-ink">/ 100</span>
                  </div>
                </div>
                <div className="text-[14px] text-muted-ink leading-snug">
                  <div className="text-ink font-semibold">Cove - B2B AI infra</div>
                  <div>Seed · $2M · SF</div>
                  <div className="mt-1">Strong traction slide. Ask needs sharpening.</div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <MiniRow color="g" label="Problem & market" note="Strong" />
                <MiniRow color="g" label="Team" note="Category-fit" />
                <MiniRow color="a" label="The ask" note="Vague on use-of-funds" />
              </div>
              <div className="mt-4 pt-4 border-t border-line text-[12.5px] text-ink-tertiary">
                One-pager attached · sent to your inbox
              </div>
            </div>
          </div>
        </section>

        {/* WHAT YOU GET */}
        <section className="bg-sky px-5 py-16 sm:py-20">
          <div className="max-w-[1120px] mx-auto">
            <div className="mb-3 text-[13px] font-bold uppercase tracking-widest text-accent">
              What lands in your inbox
            </div>
            <h2 className="font-display text-[clamp(24px,3.4vw,36px)] font-semibold max-w-[760px] leading-[1.15]">
              Better dealflow, without the busywork.
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-8">
              {BENEFITS.map((b) => (
                <div
                  key={b.title}
                  className={`${toneBg[b.tone]} border border-line rounded-2xl p-5 shadow-[0_2px_0_rgba(0,0,0,0.02)]`}
                >
                  <div className="text-[15.5px] font-semibold text-ink leading-snug">{b.title}</div>
                  <p className="text-[14px] text-muted-ink mt-2 leading-[1.55]">{b.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* REGISTRATION - form-first, deferred auth */}
        <section id="claim" className="bg-surface-sunken px-5 py-16 sm:py-20">
          <div className="max-w-[720px] mx-auto">
            <div className="text-center">
              <span className="font-script text-accent text-[22px] leading-none">claim your page</span>
              <h2 className="font-display text-[clamp(26px,3.6vw,38px)] font-semibold mt-2 leading-[1.15]">
                Your private DeckScore page - free in 60 seconds.
              </h2>
              <p className="text-[15.5px] text-muted-ink mt-3 max-w-[520px] mx-auto">
                No card. No public listing. You choose exactly which founders reach you.
              </p>
            </div>

            {checking ? (
              <p className="mt-8 text-center text-ink-secondary">Checking your session…</p>
            ) : finalizing ? (
              <div className="mt-10 bg-surface border border-line rounded-2xl p-8 text-center">
                <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-line border-t-accent" />
                <div className="text-[18px] font-semibold">Finishing your registration…</div>
                <p className="text-[14px] text-ink-secondary mt-2">Taking you to your dashboard.</p>
              </div>
            ) : magicSent ? (
              <div className="mt-10 bg-surface border border-line rounded-2xl p-6 md:p-8 text-center">
                <div className="text-[18px] font-semibold">Check your email ✉️</div>
                <p className="text-[14px] text-ink-secondary mt-2">
                  We sent a sign-in link to <b className="text-ink">{magicSentTo}</b>. Click it and
                  we'll take you <b>straight to your dashboard</b> - no more forms.
                </p>
                <p className="text-[12.5px] text-ink-tertiary mt-3">
                  Wrong email? <button type="button" className="underline hover:text-ink" onClick={() => setMagicSent(false)}>Edit and resend</button>
                </p>
              </div>
            ) : (
              <form
                onSubmit={onClaim}
                className="mt-10 bg-surface border border-line rounded-2xl p-6 md:p-8 flex flex-col gap-4"
              >
                <div>
                  <label className="text-[13px] text-ink-secondary block mb-1">Firm / fund name</label>
                  <input required className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Ventures" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[13px] text-ink-secondary block mb-1">Type</label>
                    <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
                      <option value="vc">VC</option>
                      <option value="angel">Angel</option>
                      <option value="accelerator">Accelerator</option>
                      <option value="incubator">Incubator</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[13px] text-ink-secondary block mb-1">Region</label>
                    <input className={inputCls} value={region} onChange={(e) => setRegion(e.target.value)} placeholder="India, US, Global…" />
                  </div>
                </div>
                <div>
                  <label className="text-[13px] text-ink-secondary block mb-1">Thesis (optional)</label>
                  <textarea rows={3} className={inputCls + " min-h-[84px] py-2.5"} value={thesis} onChange={(e) => setThesis(e.target.value)} />
                </div>
                <div>
                  <label className="text-[13px] text-ink-secondary block mb-1">Work email (this is your login &amp; where referrals arrive)</label>
                  <input
                    type="email"
                    required
                    className={inputCls}
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder="you@fund.com"
                  />
                  <p className="text-[12px] text-ink-tertiary mt-1">We'll email you a link to verify - click it and land straight on your dashboard.</p>
                </div>

                <label className="mt-1 flex items-start gap-3 rounded-xl border border-line bg-bg p-3.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-accent"
                    checked={openToProgram}
                    onChange={(e) => setOpenToProgram(e.target.checked)}
                  />
                  <span className="text-[13.5px] text-ink leading-snug">
                    <b className="font-semibold">Optional:</b> I'm open to DeckScore matching me with relevant founders. Only after you review and approve me - never automatically.
                    <span className="block text-[12.5px] text-ink-tertiary mt-1">
                      Leave this off and you'll only ever receive decks from founders you personally share your page with.
                    </span>
                  </span>
                </label>

                <button type="submit" disabled={saving} className={primaryBtn + " mt-2 self-start"}>
                  {saving ? "Sending…" : "Claim my dealflow page →"}
                </button>
                <p className="text-[12.5px] text-ink-tertiary">
                  Free &amp; private. You choose who gets your page - we never list you publicly.
                </p>
                {error && <p className="text-[13px] text-[#C65C4E]">{error}</p>}
              </form>
            )}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function MiniRow({ color, label, note }: { color: "g" | "a" | "r"; label: string; note: string }) {
  const dot = color === "g" ? "bg-[#4CAF7A]" : color === "a" ? "bg-[#E0A22C]" : "bg-[#C65C4E]";
  return (
    <div className="flex items-center justify-between text-[13px]">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <span className="text-ink">{label}</span>
      </div>
      <span className="text-ink-tertiary">{note}</span>
    </div>
  );
}
